if (!customElements.get("product-description-read-more")) {
  customElements.define(
    "product-description-read-more",
    class ProductDescriptionReadMore extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector(".product-description-read-more-button");
        this.label = this.querySelector(".product-description-read-more-label");
        this.expandable = this.closest("[data-product-description-expandable]");
        this.content = this.expandable?.querySelector("[data-product-description-content]");

        if (!this.button || !this.content || !this.expandable) {
          return;
        }

        this.linesToShow = parseInt(this.dataset.lines, 10) || 18;
        this.readMoreText = this.dataset.readMore || "View More";
        this.readLessText = this.dataset.readLess || "Show Less";
        this.collapsible = this.closest("collapsible-expandable");
        this.onResize = this.debounce(() => this.updateDescription(), 120);
        this.onToggleClick = () => this.handleToggleClick();

        this.button.addEventListener("click", this.onToggleClick);
        window.addEventListener("resize", this.onResize);

        if (this.collapsible) {
          this.collapsibleObserver = new MutationObserver(() => {
            if (this.collapsible.getAttribute("expanded") === "true") {
              window.requestAnimationFrame(() => this.updateDescription());
            }
          });
          this.collapsibleObserver.observe(this.collapsible, {
            attributes: true,
            attributeFilter: ["expanded"],
          });
        }

        window.requestAnimationFrame(() => this.updateDescription());
      }

      disconnectedCallback() {
        this.button?.removeEventListener("click", this.onToggleClick);
        window.removeEventListener("resize", this.onResize);
        this.collapsibleObserver?.disconnect();
      }

      calculateVisibleHeight(element, linesToShow) {
        const computedStyle = window.getComputedStyle(element);
        let lineHeight = parseFloat(computedStyle.lineHeight);

        if (Number.isNaN(lineHeight)) {
          lineHeight = parseFloat(computedStyle.fontSize) * 1.5;
        }

        const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
        const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;

        return lineHeight * linesToShow + paddingTop + paddingBottom;
      }

      setExpandedState(isExpanded) {
        this.expandable.classList.toggle("is-expanded", isExpanded);
        this.expandable.classList.toggle("is-collapsed", !isExpanded);
        this.content.classList.toggle("is-expanded", isExpanded);
        this.content.classList.toggle("is-collapsed", !isExpanded);
        this.button.setAttribute("aria-expanded", isExpanded ? "true" : "false");

        if (this.label) {
          this.label.textContent = isExpanded ? this.readLessText : this.readMoreText;
        }
      }

      updateDescription() {
        if (!this.content || !this.button || !this.expandable) {
          return;
        }

        const isExpanded = this.content.classList.contains("is-expanded");
        this.content.style.height = "auto";

        const collapsedHeight = this.calculateVisibleHeight(this.content, this.linesToShow);
        const contentHeight = this.content.scrollHeight;

        if (contentHeight <= Math.ceil(collapsedHeight) + 1) {
          this.hidden = true;
          this.expandable.classList.remove("is-expanded", "is-collapsed");
          this.content.style.height = "auto";
          this.content.classList.remove("is-expanded", "is-collapsed");
          this.button.setAttribute("aria-expanded", "false");
          if (this.label) {
            this.label.textContent = this.readMoreText;
          }
          return;
        }

        this.hidden = false;

        if (isExpanded) {
          this.content.style.height = `${contentHeight}px`;
          this.setExpandedState(true);
          return;
        }

        this.content.style.height = `${Math.ceil(collapsedHeight)}px`;
        this.setExpandedState(false);
      }

      handleToggleClick() {
        if (!this.content) {
          return;
        }

        const isExpanded = this.content.classList.contains("is-expanded");

        if (isExpanded) {
          const collapsedHeight = this.calculateVisibleHeight(this.content, this.linesToShow);
          this.content.style.height = `${this.content.offsetHeight}px`;
          this.content.offsetHeight;
          this.content.style.height = `${Math.ceil(collapsedHeight)}px`;
          this.setExpandedState(false);
          return;
        }

        const fullHeight = this.content.scrollHeight;
        const collapsedHeight = this.calculateVisibleHeight(this.content, this.linesToShow);
        this.content.style.height = `${Math.ceil(collapsedHeight)}px`;
        this.content.offsetHeight;
        this.setExpandedState(true);
        this.content.style.height = `${fullHeight}px`;
      }

      debounce(func, wait) {
        let timeout;

        return (...args) => {
          window.clearTimeout(timeout);
          timeout = window.setTimeout(() => func.apply(this, args), wait);
        };
      }
    },
  );
}
